import React from "react";

import classnames from "classnames";
import { calculateMaxEstimatedCost } from "ComparePlans/calculateCost";
import SBCLinkCell from "ComparePlans/Components/SBCLinkCell";
import PropTypes from "prop-types";
import { NETWORK_DESCRIPTIONS } from "QuotePage/NetworkDescriptions";
import { displayDoctorNetworkInfo } from "QuotePage/NetworkSizeConstants";
import formatCurrency from "Utils/formatCurrency";
import getTooltip from "Utils/getTooltip";

import { Popover } from "Cobalt/Popover";
import { Table, TR, TD } from "Cobalt/TableV2";
import BodyText from "Common/BodyText";
import { Card, CardHeader, CardSection } from "Common/Card";
import SIOverlay from "Common/SIOverlay";
import SIOverlayTrigger from "Common/SIOverlayTrigger";

const PlanOverview = ({ plan, tableProperties, BottomLink, dashboard }) => {
  const propertiesForEmployer = [
    { label: "Network Type", key: "plan_type" },
    { label: "Deductible", key: "deductible" },
    { label: "Annual Out-of-Pocket Limit", key: "oop_limit" },
    { label: "Coinsurance", key: "coinsurance" },
    { label: "Health Savings Account (HSA) Eligible", key: "hsa_eligible" },
  ];

  const propertiesForEmployee = [
    { label: "Network Type", key: "plan_type" },
    { label: "Deductible", key: "deductible" },
    { label: "Annual Out-of-Pocket Limit", key: "oop_limit" },
    { label: "Estimated Doctor Visit", key: "pcp_visit" },
    { label: "Estimated Prescription Drugs", key: "generic_drugs" },
    { label: "Estimated Hospitalization", key: "hospitalization" },
  ];

  const tableConfiguration = tableProperties === "employee" ? propertiesForEmployee : propertiesForEmployer;

  const tdClassName = dashboard ? "w-2/5" : "w-1/2";

  if (displayDoctorNetworkInfo(plan.state)) {
    tableConfiguration.unshift({
      label: "Plan Network",
      key: "network",
    });
  }

  function calculateDisplayValues(individual, family) {
    if (!family) {
      return `${formatCurrency(individual)} per individual, ${formatCurrency(individual * 2)} per family`;
    }
    return `${formatCurrency(individual)} per individual, ${formatCurrency(family)} per family`;
  }

  const rows = tableConfiguration.map((config, i) => {
    if (config.label === "Plan Network" || config.label === "Estimated Prescription Drugs" || plan.fine_print[config.key]) {
      let planValue = plan.fine_print[config.key] || plan[config.key];

      if (config.label === "Network Type") {
        const planKeyPopover = (
          <Popover showBackdrop={false} className="plan-term-tooltip" id={planValue}>
            {NETWORK_DESCRIPTIONS[planValue]}
          </Popover>
        );

        const target = <span className="SIOverlayTrigger__base">{planValue}</span>;

        return (
          <TR className="content-row" key={i} highlightFirstCell={false}>
            <TD className={tdClassName}>{config.label}</TD>
            <TD>
              <SIOverlay placement="bottom" overlayContent={planKeyPopover} overlayTarget={target} trackType="dashed-plan-term" trackId={planValue} />
            </TD>
          </TR>
        );
      }
      if (config.label === "Plan Network") {
        return (
          <TR className="content-row" key={i} highlightFirstCell={false}>
            <TD className={tdClassName}>{config.label}</TD>
            <TD>{planValue}</TD>
          </TR>
        );
      }
      if (config.label === "Deductible") {
        planValue = calculateDisplayValues(plan.deductible, plan.family_deductible);
      } else if (config.label === "Annual Out-of-Pocket Limit") {
        planValue = calculateDisplayValues(plan.limit, plan.family_limit);
      } else if (config.label === "Estimated Doctor Visit") {
        planValue = formatCurrency(plan.estimated_costs.pcp_visit);
      } else if (config.label === "Estimated Prescription Drugs") {
        planValue = `${formatCurrency(plan.estimated_costs.generic_drugs)} - ${formatCurrency(plan.estimated_costs.brand_drugs)}`;
      } else if (config.label === "Estimated Hospitalization") {
        planValue = formatCurrency(calculateMaxEstimatedCost(plan, "hospitalization"));
      }

      const planKeyPopover = (
        <Popover showBackdrop={false} className="plan-term-tooltip" id={config.label}>
          {getTooltip(config.label)}
        </Popover>
      );

      return (
        <TR className="content-row" key={i} highlightFirstCell={false}>
          <TD className={tdClassName}>
            <SIOverlayTrigger placement="bottom" overlay={planKeyPopover} trackType="dashed-plan-term" trackId={config.label}>
              <span className="SIOverlayTrigger__base">{config.label}</span>
            </SIOverlayTrigger>
          </TD>
          <TD>{planValue}</TD>
        </TR>
      );
    }
  });

  return (
    <Card theme="flat" className={classnames("section-holder")} id="planOverview" p={0}>
      <CardHeader>Plan Overview</CardHeader>
      <CardSection>
        <BodyText>
          <Table striped id={`plan-overview-${plan.id}`} borders="none">
            <tbody>
              {rows}
              {dashboard && (
                <TR className="content-row" highlightFirstCell={false}>
                  <TD className="w-2/5">Plan Document</TD>
                  <TD>
                    <SBCLinkCell plan={plan} />
                  </TD>
                </TR>
              )}
            </tbody>
          </Table>
        </BodyText>
        {BottomLink && <BottomLink />}
      </CardSection>
    </Card>
  );
};

PlanOverview.propTypes = {
  plan: PropTypes.object,
};

export default PlanOverview;
